import { AppBar, Toolbar, IconButton, Typography, Button, Menu, MenuItem, ListItemIcon, ListItemText, useTheme, useMediaQuery, Badge } from "@mui/material";
import i18next from "i18next";
import { AuthStore } from "../stores/AuthStore";
import {
  ExpandLess,
  ExpandMore,
  AccountCircle,
  Language as LanguageIcon,
  Chat as ChatIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import MenuIcon from '@mui/icons-material/Menu';
import React from "react";

import { NaviMenuItem } from './NaviMenuItem';
import { supportedLanguages, changeLanguage, tEnum } from '../lang';

type HeaderProps = {
  toggleDrawer: () => void;
  drawerOpen: boolean;
  navigate: (path: string) => void;
  authStore: AuthStore;

  adminMenuItems: NaviMenuItem[];
  toggleMenu: (id: string) => void;
  menuOpenState: { [key: string]: boolean };
  isMobile?: boolean;
  pageTitle?: string;
  badgeCount?: number;
  onOpenMessenger?: () => void;
}

export default function Header({ toggleDrawer, drawerOpen, navigate, authStore, adminMenuItems, toggleMenu, menuOpenState, isMobile, pageTitle, badgeCount = 0, onOpenMessenger }: HeaderProps) {

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [adminMenuAnchorEl, setAdminMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [langAnchorEl, setLangAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleAvatarClick = () => {
    if (badgeCount > 0 && onOpenMessenger) {
      onOpenMessenger();
    }
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLangMenu = (event: React.MouseEvent<HTMLElement>) => {
    setLangAnchorEl(event.currentTarget);
  };

  const handleLangClose = () => {
    setLangAnchorEl(null);
  };

  const handleLanguageChange = (langCode: string) => {
    changeLanguage(langCode);
    handleLangClose();
    window.location.reload();
  };


  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAdminMenuClose = () => {
    setAdminMenuAnchorEl(null);
  };

  const handleLogout = () => {
    authStore.logout();
    navigate('/');
    handleClose();
  };

  const handleProfile = () => {
    navigate('/profile');
    handleClose();
  };


  return (
    <AppBar position="sticky" color="default" elevation={0} sx={{
      backgroundColor: 'rgba(255, 224, 53, 0.95)', // Primary Yellow with slight transparency
      backdropFilter: 'blur(5px)',
      borderBottom: 'none',
      boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
      zIndex: (theme) => theme.zIndex.drawer - 1,
      width: { xs: '100%', md: drawerOpen ? "calc(100% - 240px)" : "100%" },
      ml: { xs: 0, md: drawerOpen ? '240px' : 0 },
      transition: 'all 0.3s ease',
    }}>
      <Toolbar>
        <IconButton
          edge="start"
          color="inherit"
          aria-label="menu"
          onClick={toggleDrawer}
          disabled={!authStore.isAuthenticated}
          sx={{
            mr: 2,
            display: drawerOpen && !isMobile ? 'none' : 'inline-flex'
          }}
        >
          <MenuIcon />
        </IconButton>
        {pageTitle && authStore.isAuthenticated ? (
          <div style={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            <HomeIcon
              sx={{ fontSize: '1.3rem', cursor: 'pointer', mr: 0.3, color: 'inherit', '&:hover': { opacity: 0.7 } }}
              onClick={() => navigate('/')}
            />
            <Typography
              variant="body2"
              sx={{ cursor: 'pointer', '&:hover': { opacity: 0.7 } }}
              onClick={() => navigate('/')}
            >
              {i18next.t('nav.home')}
            </Typography>
            <NavigateNextIcon sx={{ fontSize: '1.2rem', mx: 0.3, color: 'rgba(0,0,0,0.5)' }} />
            <Typography variant="h6" component="span">
              {pageTitle}
            </Typography>
          </div>
        ) : (
          <Typography
            variant="h6"
            component="div"
            sx={{ flexGrow: 1, cursor: 'pointer' }}
            onClick={() => navigate('/')}
          >
            {pageTitle || (isMobile ? i18next.t('top.site_name_short') : i18next.t('top.site_description'))}
          </Typography>
        )}

        {/* 언어 선택 메뉴 */}
        <IconButton
          size="small"
          aria-label="language selector"
          aria-controls="language-menu"
          aria-haspopup="true"
          onClick={handleLangMenu}
          color="inherit"
          sx={{ mr: 1 }}
        >
          <LanguageIcon />
        </IconButton>
        <Menu
          id="language-menu"
          anchorEl={langAnchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={Boolean(langAnchorEl)}
          onClose={handleLangClose}
          sx={{
            mt: 1,
            '& .MuiMenuItem-root': {
              fontSize: '0.85rem',
            }
          }}
        >
          {supportedLanguages.map((lang) => (
            <MenuItem
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              selected={i18next.language === lang.code}
              sx={lang.code !== 'ja' ? { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: 0.5 } : {}}
            >
              {lang.nativeName}
              {lang.code !== 'ja' && (
                <span style={{ fontSize: '0.65rem', color: '#e65100', lineHeight: 1.2 }}>
                  ※自動翻訳
                </span>
              )}
            </MenuItem>
          ))}
        </Menu>

        {authStore.user ? (
          <>
            {/* Admin 메뉴 */}
            {authStore.user.level === 'ADMIN' && (
              <>
                <Menu
                  anchorEl={adminMenuAnchorEl}
                  open={Boolean(adminMenuAnchorEl)}
                  onClose={handleAdminMenuClose}
                  sx={{
                    mt: 1,
                    '& .MuiMenuItem-root': {
                      fontSize: '0.7rem',
                      '& .MuiListItemText-primary': {
                        fontSize: '0.7rem'
                      }
                    }
                  }}
                >
                  {adminMenuItems.map((item) => (
                    item.subItems ? (
                      <MenuItem
                        key={item.id}
                        onClick={() => toggleMenu(item.id!)}
                      >
                        <ListItemIcon sx={{ minWidth: 35 }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText>
                          {item.text}
                        </ListItemText>
                        {menuOpenState[item.id!] ? <ExpandLess /> : <ExpandMore />}
                      </MenuItem>
                    ) : (
                      <MenuItem
                        key={item.path}
                        onClick={() => {
                          navigate(item.path!);
                          handleAdminMenuClose();
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 35 }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText>
                          {item.text}
                        </ListItemText>
                      </MenuItem>
                    )
                  ))}
                </Menu>
              </>
            )}

            {/* アバター（未読メッセージがある場合のみクリック可能） */}
            <IconButton
              size="small"
              aria-label="messenger"
              onClick={handleAvatarClick}
              color="inherit"
              sx={{
                cursor: badgeCount > 0 ? 'pointer' : 'default',
                '&:hover': badgeCount > 0 ? {} : { backgroundColor: 'transparent' },
              }}
              disableRipple={badgeCount === 0}
            >
              <Badge badgeContent={badgeCount} color="error" max={99} overlap="circular">
                <AccountCircle />
              </Badge>
            </IconButton>
            {/* ユーザー名メニュー */}
            {!isMobile && (
              <Button
                size="small"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleUserMenuOpen}
                color="inherit"
                sx={{ fontSize: '0.8rem', textTransform: 'none', ml: 0.5 }}
              >
                {`${authStore.user.username} (${tEnum(authStore.user.level)})`}
              </Button>
            )}
            {isMobile && (
              <IconButton
                size="small"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleUserMenuOpen}
                color="inherit"
              >
                <ExpandMore sx={{ fontSize: '1rem' }} />
              </IconButton>
            )}
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              sx={{
                mt: 1,
                '& .MuiMenuItem-root': {
                  fontSize: '0.7rem',
                  '& .MuiListItemText-primary': {
                    fontSize: '0.7rem'
                  }
                }
              }}
            >
              <MenuItem onClick={handleProfile} sx={{ fontSize: '0.7rem' }}>
                <ListItemIcon sx={{ minWidth: 30 }}><PersonIcon sx={{ fontSize: '1rem' }} /></ListItemIcon>
                {i18next.t('nav.profile')}
              </MenuItem>
              {/* メッセンジャー機能非表示 */}
              {/* <MenuItem onClick={() => { handleClose(); onOpenMessenger?.(); }} sx={{ fontSize: '0.7rem' }}>
                <ListItemIcon sx={{ minWidth: 30 }}><ChatIcon sx={{ fontSize: '1rem' }} /></ListItemIcon>
                {i18next.t('messenger.title')}
              </MenuItem> */}
              <MenuItem onClick={handleLogout} sx={{ fontSize: '0.7rem' }}>
                <ListItemIcon sx={{ minWidth: 30 }}><LogoutIcon sx={{ fontSize: '1rem' }} /></ListItemIcon>
                {i18next.t('nav.logout')}
              </MenuItem>
            </Menu>
          </>
        ) : (
          <Button
            color="inherit"
            onClick={() => navigate('/login')}
            sx={{ ml: 2, fontSize: '0.8rem' }}
          >
            {i18next.t('auth.login')}
          </Button>
        )}
      </Toolbar>
    </AppBar>
  );
}
